import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Package, CheckCircle, Loader2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  full_name: string; email: string; phone: string;
  street: string; city: string; postal_code: string;
  country: string; notes: string;
}

const EMPTY: FormData = {
  full_name: '', email: '', phone: '',
  street: '', city: '', postal_code: '',
  country: 'Polska', notes: '',
};

export default function CheckoutModal({ open, onClose }: CheckoutModalProps) {
  const { user, profile } = useAuth();
  const { items, totalPrice, clearCart } = useCart();
  const [form, setForm] = useState<FormData>({ ...EMPTY, email: user?.email ?? '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [orderId, setOrderId] = useState('');

  const set = (k: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const required: (keyof FormData)[] = ['full_name', 'email', 'street', 'city', 'postal_code'];
    for (const k of required) {
      if (!form[k].trim()) { toast.error('Wypełnij wszystkie wymagane pola'); return; }
    }
    setSubmitting(true);

    // 1. Create order
    const { data: order, error: orderErr } = await supabase
      .from('orders')
      .insert({ user_id: user.id, total_pln: totalPrice, ...form })
      .select('id')
      .single();

    if (orderErr || !order) {
      toast.error('Błąd przy tworzeniu zamówienia');
      setSubmitting(false); return;
    }

    // 2. Insert order items
    const { error: itemsErr } = await supabase.from('order_items').insert(
      items.map(i => ({
        order_id: order.id,
        figurine_id: i.figurine_id,
        title: i.title,
        price_pln: i.price_pln ?? 0,
        quantity: i.quantity,
        image_url: i.image_url,
      }))
    );

    if (itemsErr) {
      toast.error('Błąd przy zapisywaniu pozycji zamówienia');
      setSubmitting(false); return;
    }

    // 3. Clear cart
    await clearCart();
    setOrderId(order.id.slice(0, 8).toUpperCase());
    setDone(true);
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { setDone(false); setForm({ ...EMPTY, email: user?.email ?? '' }); }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div key="co-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'oklch(0.05 0.01 280 / 0.8)', backdropFilter: 'blur(6px)' }}
            onClick={!submitting ? handleClose : undefined}>

            <motion.div key="co-panel"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', stiffness: 340, damping: 38 }}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border shadow-2xl"
              style={{ background: 'oklch(0.12 0.026 280)', borderColor: 'oklch(0.25 0.045 280)' }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
                style={{ background: 'oklch(0.12 0.026 280)', borderColor: 'oklch(0.22 0.04 280)' }}>
                <div className="flex items-center gap-2.5">
                  <Package className="w-5 h-5 text-primary" />
                  <span className="font-black tracking-widest text-foreground"
                    style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    {done ? 'ZAMÓWIENIE ZŁOŻONE' : 'KASA'}
                  </span>
                </div>
                {!submitting && (
                  <button onClick={handleClose} className="text-muted-foreground hover:text-foreground transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {done ? (
                /* ── SUCCESS ── */
                <div className="flex flex-col items-center py-14 px-6 gap-5 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 260, delay: 0.1 }}>
                    <CheckCircle className="w-20 h-20 text-green-400" />
                  </motion.div>
                  <h2 className="text-2xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    DZIĘKUJEMY!
                  </h2>
                  <p className="text-muted-foreground" style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '1rem' }}>
                    Twoje zamówienie zostało przyjęte i będzie wkrótce przetworzone.
                  </p>
                  <div className="px-4 py-2 rounded-lg border text-sm font-bold"
                    style={{ fontFamily: 'JetBrains Mono, monospace', background: 'oklch(0.16 0.03 280)', borderColor: 'oklch(0.28 0.05 280)', color: 'var(--primary)' }}>
                    # {orderId}
                  </div>
                  <Button onClick={handleClose} className="mt-2 gap-2"
                    style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
                    Zamknij <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                /* ── FORM ── */
                <form onSubmit={handleSubmit} className="p-6 space-y-6">

                  {/* Order summary */}
                  <div className="rounded-xl border p-4 space-y-2"
                    style={{ background: 'oklch(0.15 0.03 280)', borderColor: 'oklch(0.22 0.04 280)' }}>
                    <h3 className="text-xs font-bold tracking-widest text-muted-foreground mb-3"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}>PODSUMOWANIE</h3>
                    {items.map(i => (
                      <div key={i.figurine_id} className="flex justify-between text-sm">
                        <span className="text-foreground truncate max-w-[60%]" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                          {i.title} <span className="text-muted-foreground">×{i.quantity}</span>
                        </span>
                        <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--primary)' }}>
                          {((i.price_pln ?? 0) * i.quantity).toFixed(2)} zł
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t mt-2"
                      style={{ borderColor: 'oklch(0.22 0.04 280)' }}>
                      <span className="font-black text-sm text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>RAZEM</span>
                      <span className="font-black text-lg" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--primary)' }}>
                        {totalPrice.toFixed(2)} zł
                      </span>
                    </div>
                  </div>

                  {/* Personal data */}
                  <div>
                    <h3 className="text-xs font-bold tracking-widest text-muted-foreground mb-4"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}>DANE KONTAKTOWE</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Imię i nazwisko *" value={form.full_name} onChange={set('full_name')} placeholder="Jan Kowalski" />
                      <Field label="E-mail *" value={form.email} onChange={set('email')} placeholder="jan@example.com" type="email" />
                      <Field label="Telefon" value={form.phone} onChange={set('phone')} placeholder="+48 600 000 000" className="sm:col-span-2" />
                    </div>
                  </div>

                  {/* Shipping */}
                  <div>
                    <h3 className="text-xs font-bold tracking-widest text-muted-foreground mb-4"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}>ADRES DOSTAWY</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Ulica i numer *" value={form.street} onChange={set('street')} placeholder="ul. Przykładowa 1/2" className="sm:col-span-2" />
                      <Field label="Miasto *" value={form.city} onChange={set('city')} placeholder="Warszawa" />
                      <Field label="Kod pocztowy *" value={form.postal_code} onChange={set('postal_code')} placeholder="00-001" />
                      <Field label="Kraj" value={form.country} onChange={set('country')} placeholder="Polska" className="sm:col-span-2" />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-bold tracking-widest text-muted-foreground mb-2"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}>UWAGI (opcjonalnie)</label>
                    <textarea
                      value={form.notes} onChange={set('notes')} rows={3}
                      placeholder="Dodatkowe informacje do zamówienia..."
                      className="w-full rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none resize-none border focus:border-primary transition-colors"
                      style={{ background: 'oklch(0.16 0.03 280)', borderColor: 'oklch(0.25 0.045 280)', fontFamily: 'Rajdhani, sans-serif' }}
                    />
                  </div>

                  <Button type="submit" disabled={submitting} className="w-full gap-2 font-black tracking-wider py-6 text-sm"
                    style={{ fontFamily: 'Orbitron, sans-serif', background: 'linear-gradient(135deg, var(--primary), var(--accent))', boxShadow: '0 0 24px oklch(0.65 0.32 280 / 0.35)' }}>
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> PRZETWARZANIE...</> : <><Package className="w-4 h-4" /> ZŁÓŻ ZAMÓWIENIE</>}
                  </Button>
                </form>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', className = '' }: {
  label: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string; type?: string; className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-bold tracking-widest text-muted-foreground mb-1.5"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</label>
      <Input type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="border-border/60 bg-[oklch(0.16_0.03_280)] text-foreground placeholder:text-muted-foreground/40 focus:border-primary"
        style={{ fontFamily: 'Rajdhani, sans-serif' }} />
    </div>
  );
}
